import type { EventDto } from './types';

/** The surfaces that render authored event copy. The confirmation email is
 *  deliberately absent: it still reads `title`/`subtitle` directly. */
export type EventSurface = 'banner' | 'modal';

export interface EventSurfaceContent {
    readonly title: string;
    /** Null when this surface should render no subtitle block at all. */
    readonly subtitle: string | null;
    /** A rich subtitle to render in place of `subtitle`. Always null for the
     *  banner, which is a single-line strip. */
    readonly subtitleDoc: unknown;
}

function nonEmpty(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
}

/** Resolves what a surface should show. `modalTitle` and `modalSubtitleDoc`
 *  are overrides where null means "inherit", so an event authored before those
 *  columns existed resolves to exactly what it rendered then. */
export function resolveEventSurface(event: EventDto, surface: EventSurface): EventSurfaceContent {
    if (surface === 'banner') {
        return { title: event.title, subtitle: nonEmpty(event.subtitle), subtitleDoc: null };
    }

    const title = nonEmpty(event.modalTitle) ?? event.title;

    // Compared against `false` so a payload predating the column (undefined)
    // keeps showing the subtitle rather than silently dropping it.
    if (event.modalShowSubtitle === false) {
        return { title, subtitle: null, subtitleDoc: null };
    }

    return {
        title,
        subtitle: nonEmpty(event.subtitle),
        subtitleDoc: event.modalSubtitleDoc ?? null
    };
}
